// src/components/StockUpdateToastManager.js
// Manages toast notifications for stocktake and stock injection updates

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import stockUpdateNotificationService from '../services/stockUpdateNotificationService';
import { showAdminDesktopNotificationIfAllowed } from '../services/adminDesktopNotificationService';
import {
    playWebAudioFeedback,
    resolveStockUpdateFeedbackKind,
} from '../utils/orderQrScanFeedback';
import ToastNotification from './ToastNotification';
import './ToastNotification.css';

const db = getFirestore();
const SHOWN_NOTIFICATIONS_KEY = 'shown_stock_notifications';
const MAX_SHOWN_NOTIFICATIONS = 1000; // Keep last 1000 shown notifications

/**
 * Check if user is Admin or Production role only
 */
function isAdminOrProduction(userData) {
    if (!userData || !userData.role) return false;
    
    const role = userData.role;
    const roles = typeof role === 'string' 
        ? role.split(',').map(r => r.trim())
        : Array.isArray(role) ? role : [role];
    
    // Only Admin and Production roles can see notifications
    return roles.some(r => r === 'Admin' || r === 'Production');
}

/**
 * Get shown notification IDs from localStorage
 */
function getShownNotifications() {
    try {
        const stored = localStorage.getItem(SHOWN_NOTIFICATIONS_KEY);
        if (stored) {
            return new Set(JSON.parse(stored));
        }
    } catch (error) {
        console.error('Error loading shown notifications:', error);
    }
    return new Set();
}

/**
 * Mark a notification as shown in localStorage
 */
function markNotificationAsShown(notificationId) {
    try {
        const shown = getShownNotifications();
        shown.add(notificationId);
        
        // Keep only the most recent notifications (prevent localStorage from growing too large)
        const shownArray = Array.from(shown);
        if (shownArray.length > MAX_SHOWN_NOTIFICATIONS) {
            const trimmed = shownArray.slice(-MAX_SHOWN_NOTIFICATIONS);
            localStorage.setItem(SHOWN_NOTIFICATIONS_KEY, JSON.stringify(trimmed));
        } else {
            localStorage.setItem(SHOWN_NOTIFICATIONS_KEY, JSON.stringify(shownArray));
        }
    } catch (error) {
        console.error('Error saving shown notification:', error);
    }
}

/**
 * Create a unique notification ID based on the actual data (not timestamp)
 * This ensures the same stocktake/injection only shows once
 */
function createNotificationId(type, data) {
    // For stocktakes: use the stocktake ID
    if (type === 'stocktake_completed' && data.id) {
        return `stocktake-${data.id}`;
    }
    
    // For stock injections: use movement ID
    if (type === 'stock_injection' && data.id) {
        return `injection-${data.id}`;
    }
    
    // For cheese stocktakes: use report ID
    if (type === 'cheese_stocktake_applied' && data.id) {
        return `cheese-stocktake-${data.id}`;
    }
    
    // For cheese adjustments: use adjustment ID
    if (type === 'cheese_stock_adjusted' && data.id) {
        return `cheese-adjust-${data.id}`;
    }
    
    // Fallback: create ID from data fields
    const key = `${type}-${data.id || data.sku || data.venueId || 'unknown'}`;
    return key;
}

export default function StockUpdateToastManager({ position = 'top-right' }) {
    const { user } = useAuth();
    const [userData, setUserData] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [notificationPreferences, setNotificationPreferences] = useState({
        stocktakeCompleted: true,
        stockInjection: true,
        cheeseStocktake: true,
        cheeseAdjustment: true
    });

    // Load user data
    useEffect(() => {
        if (!user?.uid) {
            setUserData(null);
            return;
        }

        const fetchUserData = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    setUserData({ uid: user.uid, ...userDoc.data() });
                } else {
                    setUserData({ uid: user.uid, role: 'Customer', Pricing: 'a' });
                }
            } catch (error) {
                console.error('Error fetching user data for stock updates:', error);
                setUserData({ uid: user.uid, role: 'Customer', Pricing: 'a' });
            }
        };

        fetchUserData();
    }, [user]);

    // Load notification preferences from localStorage
    useEffect(() => {
        try {
            const prefs = localStorage.getItem('stockUpdateNotificationPreferences');
            if (prefs) {
                setNotificationPreferences(JSON.parse(prefs));
            }
        } catch (error) {
            console.error('Error loading notification preferences:', error);
        }
    }, []);

    // Listen for stock updates
    useEffect(() => {
        if (!userData || !isAdminOrProduction(userData)) {
            return; // Only show toasts for Admin and Production users
        }

        // Start listening to stock updates
        stockUpdateNotificationService.start();

        // Subscribe to notifications
        const unsubscribe = stockUpdateNotificationService.subscribe(({ type, data, timestamp }) => {
            // Check if this notification type is enabled
            const typeMap = {
                'stocktake_completed': 'stocktakeCompleted',
                'stock_injection': 'stockInjection',
                'cheese_stocktake_applied': 'cheeseStocktake',
                'cheese_stock_adjusted': 'cheeseAdjustment'
            };

            const prefKey = typeMap[type];
            if (!prefKey || !notificationPreferences[prefKey]) {
                return; // Notification type is disabled
            }

            // Create unique notification ID based on data (not timestamp)
            const notificationId = createNotificationId(type, data);
            
            // Check if this notification has already been shown
            const shownNotifications = getShownNotifications();
            if (shownNotifications.has(notificationId)) {
                console.log(`[StockUpdateToast] Skipping already-shown notification: ${notificationId}`);
                return; // Already shown, skip it
            }

            // Mark as shown immediately to prevent duplicates
            markNotificationAsShown(notificationId);

            // Create toast notification based on type
            let toast = null;

            switch (type) {
                case 'stocktake_completed':
                    toast = {
                        id: notificationId, // Use the unique notification ID
                        title: 'Stocktake Completed',
                        message: `${data.completedBy || 'Someone'} completed a stocktake${data.venueName ? ` at ${data.venueName}` : ''}. ${data.totalSkus || 0} SKU(s), ${data.totalBatches || 0} batch(es) counted.`,
                        type: 'success',
                        duration: 8000,
                        position: position,
                        ctaText: 'View Stock',
                        ctaLink: `/ProductionToolkit?activeTab=planning&stocktake=${data.id}`,
                        ctaColor: '#B99040'
                    };
                    break;

                case 'stock_injection':
                    // Format the message with proper product name, quantity, batch, and new stock
                    const quantity = data.quantity || 0;
                    const newStock = data.newStock || 0;
                    const batchInfo = data.lotCode ? ` (Batch: ${data.lotCode})` : '';
                    const productDisplay = data.productName || data.sku || 'Unknown Product';
                    
                    toast = {
                        id: notificationId, // Use the unique notification ID
                        title: 'Stock Injection',
                        message: `${productDisplay}: ${quantity > 0 ? '+' : ''}${quantity} unit${quantity !== 1 ? 's' : ''}${batchInfo}. New total: ${newStock}`,
                        type: 'success',
                        duration: 7000,
                        position: position,
                        ctaText: 'View Stock',
                        ctaLink: `/ProductionToolkit?activeTab=planning${data.sku ? `&sku=${encodeURIComponent(data.sku)}` : ''}`,
                        ctaColor: '#B99040'
                    };
                    break;

                case 'cheese_stocktake_applied':
                    toast = {
                        id: notificationId, // Use the unique notification ID
                        title: 'Cheese Stocktake Applied',
                        message: `Stocktake applied at ${data.venueName || data.venueId}${data.staffName ? ` by ${data.staffName}` : ''}. ${data.itemsCount || 0} item(s) updated.`,
                        type: 'success',
                        duration: 7000,
                        position: position,
                        ctaText: 'View Venue',
                        ctaLink: data.venueId ? `/venues-toolkit?venue=${data.venueId}` : null,
                        ctaColor: '#B99040'
                    };
                    break;

                case 'cheese_stock_adjusted':
                    const deltaKg = (data.deltaGrams || 0) / 1000;
                    toast = {
                        id: notificationId, // Use the unique notification ID
                        title: 'Cheese Stock Adjusted',
                        message: `${data.productName || data.sku}: ${deltaKg > 0 ? '+' : ''}${deltaKg.toFixed(2)}kg. New stock: ${(data.newStock || 0) / 1000}kg`,
                        type: 'info',
                        duration: 6000,
                        position: position,
                        ctaText: data.venueId ? 'View Venue' : null,
                        ctaLink: data.venueId ? `/venues-toolkit?venue=${data.venueId}` : null,
                        ctaColor: '#B99040'
                    };
                    break;

                default:
                    return; // Unknown type
            }

            if (toast) {
                setToasts(prev => {
                    // Remove old toasts if we have too many (max 3)
                    const updated = [...prev, toast];
                    if (updated.length > 3) {
                        return updated.slice(-3);
                    }
                    return updated;
                });
                const soundKind = resolveStockUpdateFeedbackKind(type);
                if (soundKind) {
                    playWebAudioFeedback(soundKind, { dedupeKey: `stock-update:${notificationId}` });
                }
                // OS notifications only when the tab is not focused — avoids duplicate with the in-app toast (same text, different chrome).
                const desktopTypes = type === 'stocktake_completed' || type === 'stock_injection';
                if (
                    desktopTypes &&
                    typeof document !== 'undefined' &&
                    document.hidden
                ) {
                    showAdminDesktopNotificationIfAllowed(userData, toast.title, toast.message, {
                        tag: `lsc-stock-${toast.id}`,
                        onClickNavigate: toast.ctaLink || undefined,
                    });
                }
            }
        });

        return () => {
            unsubscribe();
            stockUpdateNotificationService.stop();
        };
    }, [userData, notificationPreferences, position]);

    const handleDismiss = useCallback((toastId) => {
        setToasts(prev => prev.filter(t => t.id !== toastId));
    }, []);

    const handleCTAClick = useCallback((toastId, ctaLink) => {
        // Navigation is handled by ToastNotification component
    }, []);

    // Don't render if user is not Admin or Production
    if (!userData || !isAdminOrProduction(userData)) {
        return null;
    }

    return (
        <div className={`toast-container toast-${position}`}>
            {toasts.map((toast) => (
                <ToastNotification
                    key={toast.id}
                    notification={toast}
                    onDismiss={handleDismiss}
                    onCTAClick={handleCTAClick}
                    position={position}
                />
            ))}
        </div>
    );
}
